import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useUserEventLogger } from '@repo/tracking-service';
import { Checkbox, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, List, MainTitle, SubTitle, Svg } from '@repo/ui';
import { TermDrawer } from '@repo/user-sign';
import React, { useEffect, useMemo, useState } from 'react';

import { RefundAgreementDrawerButtons } from '@/components/common/refund-agreement';

export const RefundAgreementDrawer = NiceModal.create(
  ({
    feeRate,
    defaultFeeRate,
    defaultChecked = false,
    handleSubmit
  }: {
    feeRate?: string;
    defaultFeeRate: number;
    defaultChecked: boolean;
    handleSubmit: () => void;
  }) => {
    const { visible, hide } = useModal();
    const modal = useModal(TermDrawer);
    const { pageName, sendClickEvent, sendViewEvent } = useUserEventLogger();
    const userEventPrefix = `${pageName}_terms`;

    const [termStatus, setTermStatus] = useState({
      delegation: defaultChecked,
      'delegation-contract': defaultChecked,
      'unique-identification': defaultChecked,
      'third-agree': defaultChecked
    });

    const [isPaymentNoticeChecked, setIsPaymentNoticeChecked] = useState(defaultChecked);

    const termNames: Record<
      keyof typeof termStatus,
      {
        label: string;
        description?: string;
      }
    > = {
      delegation: {
        label: '[필수] 경정청구업무 위임동의서'
      },
      'delegation-contract': {
        label: '[필수] 경정청구업무 권한 위임장',
        description: '경정청구 대리와 처리 결과 확인・수령 권한을 위임해요.'
      },
      'unique-identification': {
        label: '[필수] 고유식별정보 수집·이용동의'
      },
      'third-agree': {
        label: '[필수] 개인 및 법인(신용)정보 수집·이용 및 제3자 제공 동의'
      }
    };

    const handleToggleTerm = (key: keyof typeof termStatus) => {
      sendClickEvent(`${userEventPrefix}_${key}-ok`);
      setTermStatus((prev) => ({
        ...prev,
        [key]: !prev[key]
      }));
    };

    const handleOpenTerm = (key: keyof typeof termStatus) => {
      sendClickEvent(`${userEventPrefix}_${key}-view`);
      modal.show({
        link: `https://bznav.com/popup/terms/bznav-taxrefund-${key}`,
        closeEventName: `${userEventPrefix}_${key}-close`,
        keepMounted: true
      });
    };

    const isDisabled = useMemo(
      () => Object.entries(termStatus).some(([_, isChecked]) => !isChecked) || !isPaymentNoticeChecked,
      [termStatus, isPaymentNoticeChecked]
    );

    const handleClickAllAgreement = () => {
      sendClickEvent(`${userEventPrefix}_close_check-all`);

      setTermStatus({
        delegation: isDisabled,
        'delegation-contract': isDisabled,
        'unique-identification': isDisabled,
        'third-agree': isDisabled
      });
      setIsPaymentNoticeChecked(isDisabled);
    };

    useEffect(() => {
      if (!visible) {
        sendViewEvent(userEventPrefix, undefined, { isAllowRetry: true });
      }
    }, [visible, sendViewEvent, userEventPrefix]);

    return (
      <>
        <Drawer open={visible}>
          <DrawerContent outSideClickAction={hide}>
            <DrawerHeader>
              <MainTitle title={`환급 신청하려면\n고객님의 동의가 필요해요`} size={'small'} className={'!p-0'} />
            </DrawerHeader>
            <DrawerBody className="pb-[28px] flex gap-normal-medium">
              <SubTitle className={'!p-0'} title={'경정청구를 위한 일회성 세무대리 위임 동의로, 세무대리인 변경과는 무관해요.'} />
              <div>
                <List
                  size={'medium'}
                  title={'전체 동의'}
                  primaryAction
                  onClick={handleClickAllAgreement}
                  leadingElement={<Checkbox isBox={true} checked={!isDisabled} onClick={handleClickAllAgreement} />}
                />
                {(Object.entries(termStatus) as [keyof typeof termStatus, isChecked: boolean][]).map(([key, isChecked]) => {
                  return (
                    <List
                      key={key}
                      size={'xsmall'}
                      title={termNames[key].label}
                      description={termNames[key].description}
                      leadingElement={<Checkbox checked={isChecked} />}
                      secondaryAction={<Svg src={'ic_chevron-right.svg'} width={16} height={16} onClick={() => handleOpenTerm(key)} color={'#8A96A3'} />}
                      primaryAction
                      onClick={() => handleToggleTerm(key)}
                    />
                  );
                })}
                <List
                  size={'xsmall'}
                  title={'신고대리 이용료 후불 안내'}
                  description={`환급 신청은 무료예요. 국세청 환급 결정이 된 후, 세무사 수수료로 환급금의 ${feeRate ?? defaultFeeRate}%가 발생해요. (부가세별도)`}
                  primaryAction
                  onClick={() => setIsPaymentNoticeChecked((prev) => !prev)}
                  leadingElement={<Checkbox checked={isPaymentNoticeChecked} />}
                />
              </div>
            </DrawerBody>
            <DrawerFooter>
              <RefundAgreementDrawerButtons label={isDisabled ? '약관 및 안내에 동의해주세요' : '다음'} isDisabled={isDisabled} onClick={handleSubmit} />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </>
    );
  }
);
